import { Box, Button, TextField } from 'vopay';
import * as Yup from 'yup';

import { useEffect, useState } from 'react';
import { Formik } from 'formik';

import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import usePostPartnerLogin from '@/hooks/useApi/usePostPortalPartnerLogin';
import usePasswordChange from '@/hooks/useApi/usePostPartnerAccountUsersChangePassword';
import usePasswordDialogue from '@/hooks/useDialogues/usePartnerLoginPassword.dialogue';
import { useTranslation } from 'react-i18next';

const ChangePasswordForm = (): React.JSX.Element => {
  const [pendingPassword, setPendingPassword] = useState('');
  const {
    response: { loginUsername, isRoleViewOnly },
  } = usePartnerLogin();
  const { t } = useTranslation('translations', { keyPrefix: 'UserProfile.Password.ChangePasswordForm' });

  const [currentPasswordResponse, postCurrentPassword] = usePostPartnerLogin({});
  const [newPasswordResponse, postNewPassword] = usePasswordChange({});
  const { showDialogue: showCurrentPasswordDialogue } = usePasswordDialogue(currentPasswordResponse);
  const { showDialogue: showNewPasswordDialogue } = usePasswordDialogue(newPasswordResponse);

  useEffect(() => {
    if (currentPasswordResponse?.errorMessage) showCurrentPasswordDialogue();
    if (currentPasswordResponse?.success) updatePassword();
  }, [currentPasswordResponse]);

  useEffect(() => {
    if (newPasswordResponse) showNewPasswordDialogue();
  }, [newPasswordResponse]);

  return (
    <Box boundary="shadow" mb="lg" pt="md" pb="md" ps="md" pe="md" jc="sb">
      <Formik
        initialValues={{ currentPassword: '', newPassword: '', confirmNewPassword: '' }}
        validationSchema={createdValidationSchema()}
        validateOnChange
        validateOnBlur
        onSubmit={({ currentPassword, newPassword }) => {
          setPendingPassword(newPassword);
          postCurrentPassword({ username: loginUsername, password: currentPassword });
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid }) => (
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Box jc="sb" gap="sm">
              <Box gap="sm" wrap="wrap">
                <TextField
                  key="currentPassword"
                  name="currentPassword"
                  placeholder={t('passwordPlaceholder')}
                  preset="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.currentPassword}
                  variant="outlined"
                  error={errors.currentPassword && touched.currentPassword ? errors.currentPassword : null}
                />
                <TextField
                  key="newPassword"
                  name="newPassword"
                  placeholder={t('newPasswordPlaceholder')}
                  preset="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.newPassword}
                  variant="outlined"
                  error={errors.newPassword && touched.newPassword ? errors.newPassword : null}
                />
                <TextField
                  key="confirmNewPassword"
                  name="confirmNewPassword"
                  placeholder={t('confirmNewPasswordPlaceholder')}
                  preset="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmNewPassword}
                  variant="outlined"
                  error={errors.confirmNewPassword && touched.confirmNewPassword ? errors.confirmNewPassword : null}
                />
              </Box>
              <Button
                id="update-password"
                label={t('updateButton')}
                type="submit"
                width="140px"
                color="outlined"
                isDisabled={!isValid || isRoleViewOnly}
              />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );

  function updatePassword() {
    postNewPassword({ login: loginUsername, newPassword: pendingPassword, shouldSendEmail: false });
  }

  function createdValidationSchema() {
    return Yup.object().shape({
      currentPassword: Yup.string().required(),
      newPassword: Yup.string().required(),
      confirmNewPassword: Yup.string()
        .required()
        .oneOf([Yup.ref('newPassword'), null], t('passwordsDoNotMatchValidation')),
    });
  }
};

export default ChangePasswordForm;
